import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { removeUser } from "@/slices/userSlice";
import { removePlan } from "@/slices/planSlice";
import { useAuthStore } from "@/utils/UseAuthStore";
import useNoteStore from "@/utils/NoteStore";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import useUnsavedChangesStore from "@/utils/UnsavedChangesStore";

export function UserNav() {
  const logout = useAuthStore((state) => state.logoutWithRedirect);
  const user = useAppSelector((state) => state.user.user);
  const { setNotes } = useNoteStore();
  const dispatch = useAppDispatch();
  const isAdmin = useIsAdmin();

  const logoutAndRemoveUser = () => {
    dispatch(removeUser());
    dispatch(removePlan());
    setNotes([]);
    logout();
  };

  const { templateId = "" } = useParams<{ templateId: string }>();

  const navigate = useNavigate();
  const { setPendingNavigation } = useUnsavedChangesStore();

  const handleNavigation = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    route: string
  ): void => {
    event.preventDefault();
    if (templateId) {
      setPendingNavigation(route);
    } else {
      navigate(route);
    }
  };

  return (
    <div className="flex items-center justify-center ml-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="rounded-full p-2">
            <Avatar className="h-8 w-8">
              <AvatarImage src={user?.avatar_url} alt={user.first_name} />
              <AvatarFallback>
                {user?.first_name?.slice(0, 1)}
                {user?.last_name?.slice(0, 1)}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-56 dark:bg-gray-950"
          align="end"
          forceMount
        >
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">
                {user?.first_name + " " + user?.last_name}
              </p>
              <p className="text-xs leading-none text-muted-foreground">
                {user?.email}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <Link
              to="/dashboard/settings/account"
              onClick={(event) =>
                handleNavigation(event, "/dashboard/settings/account")
              }
            >
              <DropdownMenuItem>Settings</DropdownMenuItem>
            </Link>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logoutAndRemoveUser}>
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
